import { useState, useEffect, useCallback, useRef } from "react";
import { InfoClient } from "../sdk";
import { executeWithRetry, DefaultRetryConfigs } from "../sdk/utils/retry";
import { useSubscription } from "../context/SubscriptionContext";
import { RawOrder, OrdersResponse } from "../sdk/types/api";
import { PendingOrder, PendingOrdersConfig, transformRawOrderToPendingOrder } from "../sdk/types/data";

/**
 * Pure function to get order ID from an order object
 * @param order - The pending order object
 * @returns The order ID string
 */
function getOrderId(order: PendingOrder): string {
  return order.orderId;
}

/**
 * Hook for managing pending orders data.
 * Uses WebSocket (user_updates channel) with optional initial REST fetch.
 */
export function usePendingOrders(config: PendingOrdersConfig) {
  const useWebSocket = config.useWebSocket !== false; // Default: true
  const [orders, setOrders] = useState<PendingOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const infoClientRef = useRef<InfoClient | null>(null);
  const initialFetchDoneRef = useRef<boolean>(false);
  const previousWalletAddressRef = useRef<string | undefined>(config.walletAddress);
  const configRef = useRef(config);
  configRef.current = config;
  const { subscribe, unsubscribe, isConnected: contextConnected } = useSubscription();

  /**
   * Fetch orders from API with retry logic
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   * Uses configRef to access latest config without causing dependency loops
   */
  const fetchOrders = useCallback(async () => {
    if (!infoClientRef.current) return;

    const currentConfig = configRef.current;
    // If walletAddress is not provided, we can't fetch orders
    // The API requires an address parameter
    // Don't set error state if address is simply not yet loaded (empty string)
    // Only set error if we've attempted to fetch and it failed
    if (
      !currentConfig.walletAddress ||
      currentConfig.walletAddress.trim() === ""
    ) {
      // Clear error and orders when address is not available
      setError(null);
      setOrders([]);
      setLoading(false);
      return;
    }

    setError(null);

    try {
      const data: OrdersResponse = await executeWithRetry(
        DefaultRetryConfigs.http,
        () => infoClientRef.current!.getOrders(currentConfig.walletAddress!),
      );

      // OrdersResponse structure: { orders: RawOrder[], totalCount: number, timestamp: string | number }
      const ordersArray: RawOrder[] = data.orders || [];

      if (ordersArray.length > 0) {
        // Fetch all orders (no status filtering - component will filter based on view)
        const allOrders = ordersArray.map(transformRawOrderToPendingOrder);
        // Compare with current orders to avoid unnecessary state updates
        setOrders((prev) => {
          if (prev.length !== allOrders.length) {
            return allOrders;
          }
          // Deep comparison - only update if orders actually changed
          const prevStr = JSON.stringify(prev);
          const newStr = JSON.stringify(allOrders);
          return prevStr === newStr ? prev : allOrders;
        });
      } else {
        setOrders((prev) => (prev.length === 0 ? prev : []));
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load orders";
      setError(errorMessage);
      console.error("Failed to fetch orders after retries:", err);
    } finally {
      setLoading(false);
    }
  }, []); // Removed config.walletAddress to prevent loops - using configRef instead

  /**
   * Handle order updates from WebSocket (multi-feed orders sub-channel)
   * Updates orders by order ID - merges new/updated orders and removes completed/cancelled ones
   * Receives MultiFeedOrdersData from multi-feed (can be single order or orders array)
   * Only updates state if data has actually changed to prevent unnecessary re-renders
   */
  const handleOrdersUpdate = useCallback(
    (data: unknown) => {
      // Handle MultiFeedOrdersData or RawOrder (SubscriptionCallback receives unknown)
      let ordersToProcess: RawOrder[] = [];
      if (
        typeof data === "object" &&
        data !== null &&
        "orders" in data &&
        Array.isArray((data as { orders: unknown }).orders)
      ) {
        // Multi-feed format with orders array
        ordersToProcess = (data as { orders: RawOrder[] }).orders;
      } else if (
        typeof data === "object" &&
        data !== null &&
        ("order_id" in data || "orderId" in data)
      ) {
        // Single order update (MultiFeedOrdersData or RawOrder)
        ordersToProcess = [data as RawOrder];
      } else {
        console.warn("Received invalid order data, skipping:", data);
        return;
      }

      // Process each order
      setOrders((prev) => {
        // Create a map of existing orders by ID for efficient lookup
        const ordersMap = new Map<string, PendingOrder>();
        prev.forEach((order) => {
          const orderId = getOrderId(order);
          if (orderId) {
            ordersMap.set(orderId, order);
          }
        });

        // Process each incoming order
        for (const rawOrder of ordersToProcess) {
          // Validate that we have a valid RawOrder with orderId
          if (
            !rawOrder ||
            typeof rawOrder !== "object" ||
            !("orderId" in rawOrder) ||
            typeof rawOrder.orderId !== "string" ||
            rawOrder.orderId.length === 0
          ) {
            console.warn("Received invalid order data, skipping:", rawOrder);
            continue;
          }

          // Transform the RawOrder to PendingOrder
          const incomingOrder = transformRawOrderToPendingOrder(rawOrder);
          const id = getOrderId(incomingOrder);

          if (!id) {
            // Order without ID - log warning and skip
            console.warn("Received order without ID, skipping:", incomingOrder);
            continue;
          }

          // If order has a status that indicates it's no longer active, remove it
          // Note: incomingOrder.status is already normalized (ORDER_STATUS_ prefix removed)
          // by transformRawOrderToPendingOrder, so we check normalized values
          const status = incomingOrder.status.toUpperCase();
          const isInactive =
            status === "FILLED" ||
            status === "CANCELLED" ||
            status === "REJECTED" ||
            status === "EXPIRED" ||
            status === "CLOSED" ||
            status === "DONE";
          // Note: PARTIAL, PENDING, and OPEN are considered active (pending) statuses

          if (isInactive) {
            // Remove completed/cancelled orders
            ordersMap.delete(id);
          } else {
            // Update existing order or add new one
            ordersMap.set(id, incomingOrder);
          }
        }

        // Convert map back to array, filtering out any undefined IDs
        const updatedOrders = Array.from(ordersMap.values()).filter(
          (order) => getOrderId(order) !== undefined,
        );

        // Only update if orders actually changed
        if (prev.length !== updatedOrders.length) {
          return updatedOrders;
        }

        // Deep comparison - only update if orders actually changed
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(updatedOrders);
        return prevStr === newStr ? prev : updatedOrders;
      });
    },
    [],
  );

  // Initialize clients and set up WebSocket
  useEffect(() => {
    // Reset initial fetch flag when wallet address changes
    // This ensures a fresh REST fetch for each new wallet address
    if (config.walletAddress !== previousWalletAddressRef.current) {
      initialFetchDoneRef.current = false;
      previousWalletAddressRef.current = config.walletAddress;
    }

    // Skip initialization if walletAddress is not available
    if (!config.walletAddress || config.walletAddress.trim() === "") {
      setLoading(false);
      setError(null);
      setOrders([]);
      initialFetchDoneRef.current = false;
      return;
    }

    const infoClient = new InfoClient({
      baseURL: config.baseURL,
      sessionToken: config.authToken,
    });
    infoClientRef.current = infoClient;

    // Initial fetch from REST API only (once per wallet address)
    if (!initialFetchDoneRef.current) {
      fetchOrders().finally(() => {
        initialFetchDoneRef.current = true;
      });
    }

    setIsConnected(contextConnected);

    if (useWebSocket) {
      const marketId = config.marketId ?? "0";
      const key = `orders:${marketId}`;
      const callback = (data: unknown) => handleOrdersUpdate(data);
      subscribe(key, callback);
      return () => unsubscribe(key, callback);
    }
    return () => {};
  }, [
    config.baseURL,
    config.authToken,
    config.walletAddress,
    config.marketId,
    useWebSocket,
    contextConnected,
    subscribe,
    unsubscribe,
    fetchOrders,
    handleOrdersUpdate,
  ]);

  return {
    orders,
    loading,
    error,
    isConnected,
    refresh: fetchOrders,
  };
}

